<template>
  <ViewWrapper :breadcrumbs="breadcrumbs" is-full-height>
    <div class="user-tasks-page">
      <UserTaskHeader
        :user-id="userActivitySummary?.user?.id"
        :user-first-name="userActivitySummary?.user?.first_name"
        :user-last-name="userActivitySummary?.user?.last_name"
        :user-avatar-image="userActivitySummary?.user?.avatar"
        :item-count="userActivitySummary?.activity_tasks_progress?.count"
        :done-count="userActivitySummary?.activity_tasks_progress?.total"
        :activity-titles="activity?.title"
        :is-activity-user-loading="isuserActivitySummaryLoading"
        :is-certified="isCertified"
        :is-certified-loading="isCertifiedLoading"
        :is-certifying-user="isCertifyingUser"
        @approveUser="certifyUserForActivity"
      />

      <div class="user-tasks">
        <UserTaskList
          :tasks="userActivitySummary?.tasks"
          :selected-task-index="selectedTaskIndex"
          :is-activity-user-loading="isuserActivitySummaryLoading"
          class="user-tasks__list"
          @onSelectTask="setSelectedTask"
        />

        <UserTaskSelected
          class="user-tasks__task-selected"
          :selected-task="selectedTask"
          :selected-task-index="selectedTaskIndex"
          :total-tasks="userActivitySummary?.tasks?.length"
          :product-id="props.productId.toString()"
          :user-id="userActivitySummary?.user?.id?.toString()"
          :is-certified="isCertified"
          :is-activity-user-loading="isuserActivitySummaryLoading"
          @onPreviousTask="previousTask"
          @onNextTask="nextTask"
        />
      </div>
    </div>
  </ViewWrapper>
</template>

<script lang="ts" setup>
import { useProduct } from '@/hooks/swr/useProduct'
import { useI18n } from '@/hooks/useI18N'
import { useToast } from '@/hooks/useToast'
import { useUserActivitySummaryQuery } from '@/queries/activities/activitiesQueries'
import { usePostUserProductActivityCertification } from '@/queries/user/userMutations'
import { useUserProductStatusQuery } from '@/queries/user/userQueries'
import { ROUTE_USER_APPROVALS_ACTIVITY } from '@/router/routes/routeNames'
import type { ActivityTask } from '@/types'
import { unravel } from '@/service/jsonApi'
import { computed, ref, watch } from 'vue'
import UserTaskHeader from './user/UserTaskHeader.vue'
import UserTaskList from './user/UserTaskList.vue'
import UserTaskSelected from './user/UserTaskSelected.vue'

const i18n = useI18n()
const { doToast } = useToast()

const props = defineProps<{
  productId: number | string
  userId: number | string
}>()

const breadcrumbs = computed(() => {
  return {
    title: i18n.t('approvals.REVIEW_USER_TASKS').toString(),
    backButton: {
      name: ROUTE_USER_APPROVALS_ACTIVITY,
      params: { productId: props.productId },
    },
  }
})

const { product } = useProduct(props.productId.toString())
const activity = computed(() => {
  if (!product.value) return null
  return unravel(product.value)
})

const { data: userActivitySummary, isLoading: isuserActivitySummaryLoading } =
  useUserActivitySummaryQuery(
    props.productId.toString(),
    props.userId.toString()
  )

const { data: userProductStatus, isLoading: isCertifiedLoading } =
  useUserProductStatusQuery(props.userId.toString(), props.productId.toString())
const isCertified = ref(false)

watch(
  () => userProductStatus.value?.certified,
  (certified) => {
    if (certified !== undefined) {
      isCertified.value = certified
    }
  },
  { immediate: true }
)

const { mutateAsync, isPending: isCertifyingUser } =
  usePostUserProductActivityCertification()

const certifyUserForActivity = async () => {
  mutateAsync(
    {
      userId: props.userId.toString(),
      productId: props.productId.toString(),
    },
    {
      onSuccess: (data) => {
        if (data.meta.certification_complete) {
          isCertified.value = true // Optimistically update certification status
        }
      },
      onError: () =>
        doToast({
          theme: 'error',
          text: i18n.t('approvals.APPROVE_ERROR').toString(),
        }),
    }
  )
}

// Set selected task based on index
const selectedTaskIndex = ref<number>(0)

const selectedTask = computed<ActivityTask | null>(() => {
  if (!userActivitySummary.value?.tasks || undefined) return null
  return userActivitySummary.value.tasks[selectedTaskIndex.value]
})

const previousTask = () => {
  if (!userActivitySummary.value?.tasks) return

  if (selectedTaskIndex.value === 0) {
    setSelectedTask(userActivitySummary.value.tasks.length - 1)
  } else {
    setSelectedTask(selectedTaskIndex.value - 1)
  }
}

const nextTask = () => {
  if (!userActivitySummary.value?.tasks) return

  if (selectedTaskIndex.value === userActivitySummary.value.tasks.length - 1) {
    setSelectedTask(0)
  } else {
    setSelectedTask(selectedTaskIndex.value + 1)
  }
}

const setSelectedTask = (index: number) => {
  if (!userActivitySummary.value?.tasks) return
  selectedTaskIndex.value = index
}
</script>

<style lang="scss" scoped>
.user-tasks-page {
  display: flex;
  flex-direction: column;
  flex: 1 1 0;
  gap: $spacing-s;
  max-width: $large-desktop-width-max;
}

// Mobile first approach:
.user-tasks {
  display: flex;
  justify-content: flex-start;
  gap: $spacing-3xs;
  flex-direction: row;
  flex: 1 1 0;
  overflow: hidden;

  &__list {
    display: none;
  }

  &__task-selected {
    max-width: 100%;
    width: 100%;
    min-width: 100%;
  }
}

// Small desktop and up
@media screen and (min-width: $small-desktop-width-max) {
  .user-tasks {
    &__list {
      display: flex;
      max-width: 500px;
      min-width: 300px;
    }

    &__task-selected {
      min-width: 600px;
    }
  }
}
</style>
